import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../ts/Glob';
import { renderToStaticMarkup } from 'react-dom/server'; //renderToString
import * as appTheme from '../../../_common/platform/ts/appTheme';
import { ParameterField } from '../../../_common/platform/tsx/ParameterField';
import { ProgressBar } from '../../../_common/platform/tsx/ProgressBar';
import { TitlePositionType } from '../../../_common/platform/ts/Interfaces';
import { createRoot, Root } from 'react-dom/client';
import { IDataEditValues } from '../../../_common/platform/tsx/dataEdit/Interfaces';

import ParamsValuesEdit, {
    ILayoutGroup,
    ILayoutTab,
    ILayoutContentRow
} from '../../../_common/platform/ts/ParamsValuesEdit';

export interface IWidgetParametersEditProperties {
    id?: number;
    cid?: number;
    type?: string;
    data: ParamsValuesEdit;
    theme?: appTheme.IValuesCollection;
    loading?: string;
    selectedInternalTab?: number;
    hideTabs?: string[];
    paramOptions?: { [id: string]: any };
    readOnly?: { [id: string]: boolean };
    checked?: { [id: string]: boolean };
    fieldSelectMode?: boolean;
    parameterProps?: any[];
    titlePosition?: TitlePositionType;
    titleWidth?: number;
    verticalSpaceBetweenFields?: number;
    showErrorsAsText?: boolean;
    glob: IGlob;

    convertFieldValue?(fieldName: string, value: any): any;
    onSetFieldValues?(id: number, type: string, values: IDataEditValues): void;
    onChangeTab?(id: number, type: string, i: number): void;
    onUpdate?(): void;
    onFieldSelect?(fieldName: string): void;
}

interface IFieldParams {
    setFocus: boolean;
}

function getThemeObj(parentTheme: appTheme.IValuesCollection, props: IWidgetParametersEditProperties) {
    let widgetTheme: appTheme.IValuesCollection;

    if (props.data != null && props.data.values != null && props.data.values.theme != null) {
        widgetTheme = _.extend({}, props.data.values.theme);
    }

    if (widgetTheme == null) {
        widgetTheme = {};
    }

    const obj = appTheme.extend({}, parentTheme, widgetTheme, widgetTheme._custom || {});

    return appTheme.getThemeObj(obj);
}

function getLayoutTemplate(data: ParamsValuesEdit) {
    if (data != null) {
        const paramsLayout = data.paramsLayout;

        if (paramsLayout != null && paramsLayout.templates != null) {
            const templateName = data.layoutTemplateName;

            if (templateName != null) {
                return paramsLayout.templates[templateName];
            }
        }
    }

    return null;
}

function getLayoutTemplateFieldID(id: number, customID: string) {
    return 'layout-template-field-' + id + '-' + customID;
}

export default function WidgetParametersEdit(props: IWidgetParametersEditProperties) {
    const [currentTab, setCurrentTab] = React.useState(
        props.selectedInternalTab != null ? props.selectedInternalTab : 0
    );
    const rootContainers = React.useRef<{ [containerID: string]: Root }>({});

    React.useEffect(() => {
        const template = getLayoutTemplate(props.data);

        if (template != null) {
            const { parameters, fields } = props.data;

            if (parameters != null && fields != null) {
                const id = props.id || props.cid;
                const fieldParams: IFieldParams = {
                    setFocus: true
                };

                let index = 0;

                for (let parameterName in parameters) {
                    const field = props.data.fields.find((field) => field.name === parameterName);

                    if (field != null && field.customID != null) {
                        const containerID = getLayoutTemplateFieldID(id, field.customID);
                        const fieldContainer = document.querySelector('#' + containerID);

                        if (fieldContainer != null) {
                            const paramField = getParamField(parameterName, index++, fieldParams);

                            if (paramField != null) {
                                let root = rootContainers.current[containerID];

                                if (root == null) {
                                    rootContainers.current[containerID] = root = createRoot(fieldContainer);
                                }

                                root.render(paramField);
                            }
                        }
                    }
                }
            }
        }
    });

    const getLayoutContent = () => {
        let template = getLayoutTemplate(props.data);

        if (template == null) {
            return null;
        }

        const { parameters, fields } = props.data;

        if (parameters != null && fields != null) {
            const id = props.id || props.cid;

            for (let parameterName in parameters) {
                const field = fields.find((field) => field.name === parameterName);

                if (field != null && field.customID != null) {
                    const fieldTemplateName = '{{' + field.customID + '}}';
                    const nameStart = template.indexOf(fieldTemplateName);

                    if (nameStart >= 0) {
                        const fieldContainer = <div id={getLayoutTemplateFieldID(id, field.customID)} />;

                        template =
                            template.substring(0, nameStart) +
                            renderToStaticMarkup(fieldContainer) +
                            template.substring(nameStart + fieldTemplateName.length);
                    }
                }
            }
        }

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: template
                }}
            />
        );
    };

    const getOnTabClickHandler = (index: number) => {
        return (e) => {
            e.preventDefault();
            e.stopPropagation();

            setCurrentTab(index);

            if (typeof props.onChangeTab === 'function') {
                props.onChangeTab(props.id || props.cid, props.type, index);
            }

            if (typeof props.onUpdate === 'function') {
                props.onUpdate();
            }

            return false;
        };
    };

    const getTabHeader = (name: string, index: number) => {
        if (props.hideTabs != null && _.indexOf(props.hideTabs, name) !== -1) {
            return null;
        }

        if (name === '') {
            return null;
        }

        return (
            <li key={index} className="nav-item">
                <a
                    href="#"
                    onClick={getOnTabClickHandler(index)}
                    className={'nav-link ' + (index === currentTab ? 'active' : '')}
                >
                    {name}
                </a>
            </li>
        );
    };

    const getTabsHeader = (tabs: ILayoutTab[]) => {
        if (tabs == null) {
            return null;
        }

        const headers: React.JSX.Element[] = [];

        _.each(tabs, (obj, i) => {
            let isHidden: boolean;

            if (typeof obj.hidden === 'function') {
                isHidden = obj.hidden(props.data.values);
            }

            if (isHidden !== true) {
                const header = getTabHeader(obj.name, i);

                if (header != null) {
                    headers.push(header);
                }
            }
        });

        if (headers.length > 0) {
            return (
                <ul className="nav nav-tabs" key="nav-tab">
                    {headers}
                </ul>
            );
        } else {
            return null;
        }
    };

    const getOnChangeHandler = (fieldName: string) => {
        return (value) => {
            if (typeof props.convertFieldValue === 'function') {
                value = props.convertFieldValue(fieldName, value);
            }

            if (typeof props.onSetFieldValues === 'function') {
                const obj = {};

                obj[fieldName] = value;
                props.onSetFieldValues(props.id || props.cid, props.type, obj);
            }
        };
    };

    const getParamField = (fieldName: string, index: number, params: IFieldParams) => {
        const { data, paramOptions } = props;
        const param = data.parameters[fieldName];

        if (param == null) {
            return null;
        }

        let options = param.getOptions() || {};

        if (paramOptions != null) {
            if (paramOptions[fieldName] != null) {
                options = _.extend(options, paramOptions[fieldName]);
            }

            if (paramOptions.inHtmlContainer != null) {
                options = _.extend(options, {
                    inHtmlContainer: paramOptions.inHtmlContainer
                });
            }
        }

        let theme = getThemeObj(props.theme, props);

        const { values } = props.data;
        const directOptions = param.getDirectOptions(values[fieldName], values, theme, data) || {};

        if (directOptions != null) {
            theme = directOptions.theme;
        }

        let hidden = options && options.hidden;

        if (typeof hidden === 'function') {
            hidden = hidden(values);
        }

        if (hidden) {
            return;
        }

        const EditComponent = (param.constructor as any).EditComponent;
        const readOnly = props.readOnly != null ? props.readOnly[fieldName] || false : false;
        const isChecked = props.checked != null ? props.checked[fieldName] || false : false;
        const setFocus = params.setFocus && !readOnly;

        if (setFocus) {
            params.setFocus = false;
        }

        let placeholder: string;

        const titlePosition = options?.titlePosition || props.titlePosition;

        if (titlePosition === 'placeholder') {
            placeholder = param.title;
        }

        return (
            <ParameterField
                key={'param-field-' + index}
                param={param}
                grayedOut={false}
                checked={isChecked}
                widgetId={props.id || props.cid}
                widgetType={props.type}
                showErrorsAsText={props.showErrorsAsText}
                fieldName={fieldName}
                options={options}
                selectMode={props.fieldSelectMode}
                titlePosition={titlePosition}
                titleWidth={props.titleWidth}
                verticalSpaceBetweenFields={props.verticalSpaceBetweenFields}
                glob={props.glob}
                onSelect={props.onFieldSelect}
            >
                <EditComponent
                    {...directOptions}
                    value={values[fieldName]}
                    values={values}
                    param={param}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    options={options}
                    // Some compenents may have a theme specified as themeObj or theme.
                    themeObj={theme}
                    theme={theme}
                    isInParameterField={true}
                    setFocus={setFocus}
                    glob={param.glob}
                    onChange={getOnChangeHandler(fieldName)}
                    {...props.parameterProps}
                />
            </ParameterField>
        );
    };

    const getContentRow = (
        row: string | ILayoutContentRow[],
        rowIndex: number,
        fieldParams: IFieldParams,
        groupsNumber: number,
        rowsNumber: number
    ) => {
        if (typeof row === 'string') {
            return getParamField(row, rowIndex, fieldParams);
        }

        const spans = _.map(row, (obj, contentIndex) => {
            let content: React.JSX.Element;
            let hidden = obj.hidden;

            if (typeof hidden === 'function') {
                hidden = hidden(props.data.values);
            }

            if (hidden === true) {
                if (obj.reserveSpace === true) {
                    content = null;
                } else {
                    return;
                }
            }

            if (obj.placeholder === true) {
                const style: React.CSSProperties = {};

                if (obj.b === true) {
                    style.fontWeight = 'bold';
                }

                if (obj.i === true) {
                    style.fontStyle = 'italic';
                }

                if (obj.u === true) {
                    style.textDecoration = 'underline';
                }

                let text = obj.title;

                if (typeof text === 'function') {
                    text = text(props.data.values);
                }

                content = <span style={style}>{text}</span>;
            }

            if (typeof content === 'undefined') {
                content = getParamField(obj.field, rowIndex, fieldParams);
            }

            return (
                <div key={'tab-content-row-' + contentIndex} className={'col-sm-' + obj.span}>
                    {content}
                </div>
            );
        });

        return (
            <div
                className={'row' + (groupsNumber === 1 && rowsNumber === 1 ? ' h-100' : '')}
                key={'tab-content-row-parent-' + rowIndex}
            >
                {spans}
            </div>
        );
    };

    const getContentRows = (
        rows: (ILayoutContentRow[] | string)[],
        fieldParams: IFieldParams,
        groupsNumber: number
    ) => {
        const rowsNumber = rows.length;

        return _.map(rows, (row, index) => {
            return getContentRow(row, index, fieldParams, groupsNumber, rowsNumber);
        });
    };

    const getTabContentGroups = (groups: ILayoutGroup[]) => {
        if (groups == null) {
            return null;
        }

        const fieldParams: IFieldParams = {
            setFocus: true
        };

        const result = [];
        const groupsNumber = groups.length;

        for (let index = 0; index < groupsNumber; index++) {
            const group = groups[index];

            let hidden = group.hidden;

            if (typeof hidden === 'function') {
                hidden = hidden(props.data.values);
            }

            if (!hidden) {
                if (group.name == null) {
                    result.push(getContentRows(group.rows, fieldParams, groupsNumber));
                } else {
                    result.push(
                        <div
                            key={'panel-default-' + index}
                            className={'card mb-2' + (groupsNumber === 1 ? ' h-100' : '')}
                        >
                            <div className="card-header p-2" key={'head-' + index}>
                                {group.name}
                            </div>

                            <div className="card-body p-2" key={'body-' + index}>
                                {getContentRows(group.rows, fieldParams, groupsNumber)}
                            </div>
                        </div>
                    );
                }
            }
        }

        return result;
    };

    const getTabContent = (tab: ILayoutTab, index: number) => {
        if (index !== currentTab) {
            return;
        }

        if (props.hideTabs != null && _.indexOf(props.hideTabs, tab.name) !== -1) {
            return;
        }

        return (
            <div key={index} className={'tab-pane active' + (tab.singleMode ? ' single-mode' : '')}>
                {getTabContentGroups(tab.groups)}
            </div>
        );
    };

    const getTabsContent = (tabs: ILayoutTab[]) => {
        if (tabs == null) {
            return null;
        }

        return (
            <div className="tab-content" key="tab-content">
                {_.map(tabs, (obj, index) => {
                    let hidden = obj.hidden;

                    if (typeof hidden === 'function') {
                        hidden = hidden(props.data.values);
                    }

                    if (hidden === true) {
                        return;
                    }

                    return getTabContent(obj, index);
                })}
            </div>
        );
    };

    let content;

    if (props.loading != null) {
        content = <ProgressBar />;
    } else if (props.data == null) {
        content = (
            <p className="empty">
                <strong>{'You should select widget type to edit its configuration.'}</strong>
            </p>
        );
    } else {
        const paramsLayout = props.data.paramsLayout;

        if (paramsLayout != null) {
            content = getLayoutContent();

            if (content == null && (paramsLayout.groups != null || paramsLayout.tabs != null)) {
                content = [
                    getTabContentGroups(paramsLayout.groups),
                    getTabsHeader(paramsLayout.tabs),
                    getTabsContent(paramsLayout.tabs)
                ];
            }
        }

        if (content == null) {
            const params = props.data.parameters;

            if (params != null) {
                const fieldParams: IFieldParams = {
                    setFocus: true
                };

                content = _.map(_.keys(params), (parameterName, index) => {
                    return getParamField(parameterName, index, fieldParams);
                });
            }
        }
    }

    return <div className="widget-parameters-edit">{content}</div>;
}
